import { ColumnDef } from "@tanstack/react-table";
import { If } from "components";
import { getLinkToWithNewSearchParameter } from "helpers";
/* eslint-disable react-hooks/exhaustive-deps */
import { ModeratorRoutesPrivileges, useHasPrivilege } from "modules/common";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ItemsTable, Status } from "ui";

import { ORDER_STATUSES_LIST } from "../../constants";
import { Order } from "../../orders.types";
import { CancelOrderModal, CustomerDetailsModal } from "../modals";
import { getOrderPageColumns } from "./getOrderPageColumns";
import { getResellerProfilePageColumns } from "./getResellerProfilePageColumns";
import { OrdersTableProps } from "./OrdersTable.types";

import styles from "./OrdersTable.module.scss";

const OrdersTable = ({
  ordersList,
  sorting,
  setSorting,
  isLoading,
  refetch,
  oneResellerMode = false,
  selectedOrdersIds,
  setSelectedOrdersIds,
  onAssignDriver
}: OrdersTableProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Conversations live behind their own privilege, so an admin with Orders but
  // without AI Assistants would only get a 403 from the action.
  const canOpenConversations = useHasPrivilege(
    ModeratorRoutesPrivileges.AiAssistants
  );
  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);
  const [cancelingOrder, setCancelingOrder] = useState<Order | null>(null);
  const [customerDetailsOrder, setCustomerDetailsOrder] = useState<Order | null>(
    null
  );

  const statusesList: Status[] = ORDER_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: () => {}
  }));

  const getOrderStatusIndex = (st: string) =>
    statusesList.findIndex((status) => status.value === st);

  const columns: Array<ColumnDef<Order, Order[]>> = useMemo(() => {
    const columnsParams = {
      ordersList,
      selectedOrdersIds,
      setSelectedOrdersIds,
      openedItemOptionsIndex,
      getOrderStatusIndex,
      statusesList,
      setOpenedItemOptionsIndex,
      onCancelOrder: setCancelingOrder,
      onAssignDriver,
      onOpenCustomerDetails: setCustomerDetailsOrder,
      onOpenConversation: canOpenConversations
        ? ({ conversationId }: Order) =>
            navigate(`/ai/conversations?conversationId=${conversationId}`)
        : undefined
    };

    return oneResellerMode
      ? getResellerProfilePageColumns(columnsParams)
      : getOrderPageColumns(columnsParams);
  }, [
    ordersList,
    selectedOrdersIds,
    openedItemOptionsIndex,
    onAssignDriver,
    canOpenConversations
  ]);

  return (
    <>
      <ItemsTable
        className={styles.ordersTable}
        columns={columns}
        data={ordersList}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        skeletonHeight={65}
        getLinkUrl={({ id }) => {
          return getLinkToWithNewSearchParameter({
            searchParams,
            parameterName: "orderId",
            parameterValue: id
          });
        }}
      />

      <If condition={!!cancelingOrder}>
        <CancelOrderModal
          orderId={String(cancelingOrder?.id)}
          isOpen={!!cancelingOrder}
          onClose={() => setCancelingOrder(null)}
          refetchList={refetch}
        />
      </If>

      <If condition={!!customerDetailsOrder}>
        <CustomerDetailsModal
          orderId={String(customerDetailsOrder?.id)}
          isOpen={!!customerDetailsOrder}
          onClose={() => setCustomerDetailsOrder(null)}
          refetchList={refetch}
        />
      </If>
    </>
  );
};

export default OrdersTable;
